import { divIcon, type LatLngExpression, type LeafletMouseEvent } from "leaflet";
import { useEffect, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { reverseGeocode } from "../lib/geocode";
import type { Picked } from "./AddressAutocomplete";

const HAIFA_CENTER: LatLngExpression = [32.8156, 34.9892];

function pinIcon() {
  const svg = `
    <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 1 C 7 1 1 8 1 16 c 0 11 15 25 15 25 s 15 -14 15 -25 c 0 -8 -6 -15 -15 -15 z"
            fill="#7C3AED" stroke="#2B2F54" stroke-width="2" />
      <circle cx="16" cy="16" r="5" fill="#FFFFFF" />
    </svg>`;
  return divIcon({ html: svg, className: "nf-pin", iconSize: [32, 42], iconAnchor: [16, 40] });
}

type Props = {
  /** Current point, or null if nothing chosen yet. */
  point: { lat: number; lng: number } | null;
  /** Fired after the user clicks/drags — already reverse-geocoded. */
  onPick: (p: Picked) => void;
  height?: number;
};

/**
 * A small map where the user drops or drags a pin to set the exact venue
 * location. On every placement we reverse-geocode the coordinate so the
 * parent gets a Picked (venue_name + address + lat/lng) identical to what
 * the search autocomplete produces — the two inputs stay interchangeable.
 *
 * Google Maps can't hand a picked point back to a web app, so this is the
 * in-app equivalent of "pick a spot on the map".
 */
export function LocationPicker({ point, onPick, height = 240 }: Props) {
  const [busy, setBusy] = useState(false);

  async function place(lat: number, lng: number) {
    setBusy(true);
    try {
      const picked = await reverseGeocode(lat, lng);
      onPick(picked);
    } catch {
      // Even if reverse geocoding fails, keep the coordinate so the event
      // still has a map point; the venue name can be typed in the search box.
      onPick({ display_name: "", venue_name: "", address: "", lat, lng });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative rounded-[var(--radius-md)] overflow-hidden border border-ink-100" style={{ height }}>
      <MapContainer
        center={point ? [point.lat, point.lng] : HAIFA_CENTER}
        zoom={point ? 16 : 13}
        scrollWheelZoom
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickToPlace onPlace={place} />
        <Recenter point={point} />
        {point && (
          <Marker
            position={[point.lat, point.lng]}
            icon={pinIcon()}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const ll = (e.target as { getLatLng: () => { lat: number; lng: number } }).getLatLng();
                void place(ll.lat, ll.lng);
              },
            }}
          />
        )}
      </MapContainer>
      {busy && (
        <div className="absolute top-2 right-2 z-[400] bg-surface/90 backdrop-blur px-2 py-1 rounded-full text-[11px] font-semibold text-ink-500">
          …
        </div>
      )}
    </div>
  );
}

function ClickToPlace({ onPlace }: { onPlace: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e: LeafletMouseEvent) {
      onPlace(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/** Pan the map to a point chosen elsewhere (e.g. via the search box).
 *  Only fires when the coordinate actually changes — otherwise the map
 *  would snap back on every render and fight the user's panning. */
function Recenter({ point }: { point: { lat: number; lng: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (point) map.setView([point.lat, point.lng], Math.max(map.getZoom(), 16), { animate: true });
  }, [point?.lat, point?.lng, map]);
  return null;
}
